import React, { useState } from 'react'

import type { UploadProps } from '@fourthwall/components'
import { ButtonClean, Skeleton, Subsection, Toasts, Upload } from '@fourthwall/components'
import { useFormikContext } from '@fourthwall/utils/dist/formik'
import { getRejectionErrors } from '@utils/dropzone'

import { LiveStreamKeys } from '../../components/LiveStreamKeys/LiveStreamKeys'
import { MuxPlayer } from '../../components/MuxPlayer'
import { useResourceLiveStreamNotAttachedQuery } from '../../hooks/useResourceLiveStreamNotAttachedQuery'
import { useResourceLiveStreamQuery } from '../../hooks/useResourceLiveStreamQuery'
import { FormFields, type FormValues } from '../../types'
import * as S from './styled'

type Props = {
  disabled?: boolean
}

export const LiveStreamCreateFields = ({ disabled }: Props) => {
  const [showStreamURLAndKey, setShowStreamURLAndKey] = useState(false)

  const { values, setFieldValue } = useFormikContext<FormValues>()

  const { resourceLiveStreamNotAttachedQuery } = useResourceLiveStreamNotAttachedQuery([], {
    onSuccess: (data) => {
      setFieldValue(FormFields.liveStreamResourceId, data.id)
    },
  })

  const { resourceLiveStreamQuery } = useResourceLiveStreamQuery(
    [{ id: resourceLiveStreamNotAttachedQuery.data?.id || 1 }],
    {
      enabled: !!resourceLiveStreamNotAttachedQuery.data?.id,
      refetchInterval: 5000,
    },
  )

  if (resourceLiveStreamNotAttachedQuery.isLoading) {
    return (
      <Subsection title="Connect source">
        <Skeleton>
          <Skeleton.Shape width="100%" height={284} />
        </Skeleton>
      </Subsection>
    )
  }

  const streamURLAndKey = (
    <LiveStreamKeys
      rtmpsUrl={resourceLiveStreamNotAttachedQuery.data?.rtmpsUrl || ''}
      srtUrl={resourceLiveStreamNotAttachedQuery.data?.srtUrl || ''}
      streamKey={resourceLiveStreamNotAttachedQuery.data?.streamKey || ''}
    />
  )

  const handleFileReject: UploadProps['onFileDropRejected'] = (rejectedFiles) => {
    getRejectionErrors(rejectedFiles).forEach((rejection) => {
      Toasts.notify(rejection)
    })
  }

  const handleVideoThumbnailUpload = (file?: File) => {
    if (file) {
      setFieldValue(FormFields.videoThumbnail, file)
    }
  }

  const renderThumbnailButton = () => {
    if (!values.videoThumbnail) {
      return (
        <div>
          <Upload
            accept={{
              'image/*': [],
            }}
            maxSize={10485760}
            disabled={disabled}
            // eslint-disable-next-line react/no-unstable-nested-components
            content={() => <ButtonClean label="Upload custom thumbnail" disabled={disabled} />}
            onFileDropRejected={handleFileReject}
            onFileDrop={(files) => handleVideoThumbnailUpload(files?.[0])}
          />
        </div>
      )
    }

    return (
      <div>
        <ButtonClean
          label="Remove custom thumbnail"
          disabled={disabled}
          onClick={() => {
            setFieldValue(FormFields.videoThumbnail, undefined)
          }}
        />
      </div>
    )
  }

  if (resourceLiveStreamQuery.data?.streamStatus === 'Active') {
    return (
      <Subsection>
        <S.Container>
          <MuxPlayer
            streamType="live"
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
            playbackId={resourceLiveStreamNotAttachedQuery?.data?.playbackId || ''}
            poster={
              values.videoThumbnail ? URL.createObjectURL(values.videoThumbnail as File) : undefined
            }
            tokens={{ playback: resourceLiveStreamNotAttachedQuery?.data?.playbackToken || '' }}
          />

          <S.ButtonsContainer>
            {!showStreamURLAndKey && (
              <ButtonClean
                label="Show stream URL and key"
                withUnderline
                onClick={() => setShowStreamURLAndKey(true)}
              />
            )}
            {renderThumbnailButton()}
          </S.ButtonsContainer>

          {showStreamURLAndKey && streamURLAndKey}
        </S.Container>
      </Subsection>
    )
  }

  return (
    <Subsection title="Connect source">
      <S.Container>
        {values.videoThumbnail && (
          <img
            src={URL.createObjectURL(values.videoThumbnail as File)}
            alt="Thumbnail"
            style={{ aspectRatio: '16/9', objectFit: 'cover' }}
          />
        )}
        {streamURLAndKey}
        {renderThumbnailButton()}
      </S.Container>
    </Subsection>
  )
}
